import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PieChart, Pie, Tooltip, Cell, Sector } from 'recharts'

import { colors } from '@/theme/index.theme'
import { CurrencyCodeEnum } from '@/enums'
import { PersonalizationEngineChartType } from '@/state/personalization-engine/models'

import { getFormatNumber } from '@/utils'
import { PEGraphicTooltip } from '../tooltip'

type Props = {
  chartData: PersonalizationEngineChartType[]
}

export const PEPieChartComponent = ({ chartData }: Props) => {
  const { t } = useTranslation()

  const [activeIndex, setActiveIndex] = useState(undefined)

  const COLORS = [
    colors.dark,
    colors.darker,
    colors.cyan,
    colors.purpleLight,
    colors.purple,
    colors.pink,
    colors.gray,
    colors.white,
    colors.blue,
  ]

  const handleSumTotal = () => {
    const reducer = (accumulator: any, currentValue: any) =>
      accumulator + parseFloat(currentValue.turnover)
    const sum = chartData.reduce(reducer, 0)
    return sum.toFixed(2)
  }

  const onMouseEnter = useCallback((data, index) => {
    setActiveIndex(index)
  }, [])

  const onMouseLeave = useCallback((data, index) => {
    setActiveIndex(undefined)
  }, [])

  const renderActiveShape = (props: any) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
      props

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
      </g>
    )
  }

  return (
    <PieChart width={250} height={250} style={{ position: 'relative' }}>
      <text
        textAnchor="middle"
        x={125}
        y={115}
        dy={10}
        fill={colors.white}
        fontSize="12px"
        fontWeight="700"
      >
        {getFormatNumber(Number(handleSumTotal()), CurrencyCodeEnum.USD)}
      </text>
      <text
        textAnchor="middle"
        x={125}
        y={135}
        dy={8}
        fill={colors.white}
        fontSize="12px"
      >
        {t('personalizationEngine.totalTurnover')}
      </text>
      <Pie
        stroke="none"
        data={chartData}
        activeIndex={activeIndex}
        dataKey="percentage"
        innerRadius={70}
        outerRadius={90}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        activeShape={renderActiveShape}
        fill={colors.blue}
        isAnimationActive={false}
      >
        {chartData.map(
          (entry: PersonalizationEngineChartType, index: number) => (
            <Cell
              key={`cell-${entry.name}`}
              fill={COLORS[index % COLORS.length]}
            />
          ),
        )}
      </Pie>
      {chartData[activeIndex || 0] && (
        <Tooltip
          content={<PEGraphicTooltip data={chartData[activeIndex || 0]} />}
        />
      )}
    </PieChart>
  )
}
